import { useState } from "react";
import { isAxiosError } from "axios";
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { MatterSelectorFormField } from "@/components/ui/FormFields/MatterSelectorFormField/MatterSelectorFormField";
import type { MatterSearchResult } from "@/lib/data/requests";
import {
  type LibraryItem,
  type FileMoveSource,
  type LibraryScope,
} from "@/lib/data/requests/file-library";
import { libraryQueryKey } from "@/lib/data/react-query/file-library";
import { automationError } from "@/components/features/automations/automationError";
import { queryKeys } from "@/lib/data/static/query-keys";
import { MY_FILES_UI_ENABLED } from "./visibility";
import {
  retrieveFiles,
  updateFile,
} from "@/lib/data/requests/files/canonical-files";
import type { FileDestination } from "@/lib/data/schemas/files";
export function LibraryMoveDialog({
  item,
  onClose,
}: {
  item: FileMoveSource;
  onClose: () => void;
}) {
  const [scope, setScope] = useState<LibraryScope | "matter">(
    MY_FILES_UI_ENABLED ? "personal" : "firm",
  );
  const [path, setPath] = useState<Pick<LibraryItem, "id" | "name">[]>([]);
  const [matter, setMatter] = useState<MatterSearchResult>();
  const parentId = path.at(-1)?.id;
  const destination: FileDestination = parentId
    ? { parentId }
    : scope === "matter"
      ? { scope, matterId: matter?.id ?? "" }
      : { scope };
  const folders = useInfiniteQuery({
    queryKey: [queryKeys.files, "move-folders", destination],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => retrieveFiles(destination, pageParam),
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    enabled: scope !== "matter" || !!matter,
  });
  const client = useQueryClient();
  const refreshFiles = () =>
    Promise.all([
      client.invalidateQueries({ queryKey: libraryQueryKey }),
      client.invalidateQueries({
        queryKey: ["file-content", item.fileContentId],
      }),
      client.invalidateQueries({ queryKey: [queryKeys.firmFiles] }),
      // Matter detail includes embedded files and references, alongside its file list.
      client.invalidateQueries({ queryKey: [queryKeys.matters] }),
      client.invalidateQueries({ queryKey: [queryKeys.massTorts] }),
    ]);
  const move = useMutation({
    mutationFn: async () => {
      if (!item.fileContentId || !item.contentRevision)
        throw new Error("Close this dialog and select the file again.");
      return updateFile(item.fileContentId, {
        revision: item.contentRevision,
        destination,
      });
    },
    onSuccess: async () => {
      await refreshFiles();
      onClose();
    },
    onError: async (error) => {
      if (isAxiosError(error) && error.response?.status === 409)
        await refreshFiles();
    },
  });
  const stale = isAxiosError(move.error) && move.error.response?.status === 409;
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !move.isPending) onClose();
      }}
    >
      <DialogContent aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Move {item.name}</DialogTitle>
        </DialogHeader>
        <select
          aria-label="Destination"
          className="bg-background rounded-md border p-2"
          value={scope}
          onChange={(e) => {
            setScope(e.target.value as typeof scope);
            setPath([]);
          }}
        >
          {MY_FILES_UI_ENABLED && <option value="personal">My files</option>}
          <option value="firm">Firm files</option>
          <option value="matter">A matter</option>
        </select>
        {scope === "matter" && (
          <MatterSelectorFormField
            selectedMatter={matter}
            onMatterSelected={(selected) => {
              setMatter(selected);
              setPath([]);
            }}
            slideoverDescription="Select the matter that should own this file."
            selectionHint="Choose the matter that should contain this file."
          />
        )}
        {(scope !== "matter" || matter) && (
          <div className="max-h-64 space-y-2 overflow-auto">
            <p className="text-sm">
              {[
                scope === "matter" ? matter?.title : "Top level",
                ...path.map((p) => p.name),
              ]
                .filter(Boolean)
                .join(" / ")}
            </p>
            {path.length > 0 && (
              <Button
                variant="ghost"
                onClick={() => {
                  setPath(path.slice(0, -1));
                }}
              >
                Back
              </Button>
            )}
            {folders.data?.pages
              .flatMap((page) => page.data)
              .filter(
                (folder) =>
                  folder.isFolder && !folder.systemFolder && !folder.skillId,
              )
              .map((folder) => (
                <Button
                  key={folder.id}
                  variant="outline"
                  className="block w-full text-left"
                  onClick={() => {
                    setPath([...path, folder]);
                  }}
                >
                  {folder.name}
                </Button>
              ))}
            {folders.hasNextPage && (
              <Button
                variant="ghost"
                onClick={() => void folders.fetchNextPage()}
              >
                More folders
              </Button>
            )}
            {folders.error && (
              <p role="alert">{automationError(folders.error)}</p>
            )}
          </div>
        )}
        {scope !== "personal" && item.scope === "personal" && (
          <p className="text-sm">
            This file will become available to people who can access the
            destination.
          </p>
        )}
        {move.error && (
          <p role="alert" className="text-destructive">
            {stale
              ? "This file changed. Close this dialog and select it again to review the latest version."
              : automationError(move.error)}
          </p>
        )}
        <Button
          disabled={
            move.isPending ||
            stale ||
            !item.contentRevision ||
            folders.isPending ||
            !!folders.error ||
            (scope === "matter" && !matter)
          }
          onClick={() => {
            move.mutate();
          }}
        >
          {move.isPending ? "Moving…" : "Move here"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
